import {
  borderRadius,
  PriceChange,
  SkeletonLoader,
  typographyV2,
} from '@toggle/design-system';
import styled from 'styled-components';

export const Root = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: pointer;

  &:hover {
    background-color: var(--surface-hover);
  }

  &:first-child:hover {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child:hover {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

export const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 12px;
`;

export const AssetDetailsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AssetNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AssetNameLabel = styled.span`
  ${typographyV2.titleBase};
`;

export const AssetTickerLabel = styled.span`
  ${typographyV2.bodyXs};
  color: var(--text-soft);
`;

const priceChangeColors = {
  default: 'var(--text-soft)',
  negative: 'var(--viz-bearish)',
  positive: 'var(--viz-bullish)',
};

export const PriceChangeContainer = styled.div<{
  $status: PriceChange['status'];
}>`
  display: flex;
  gap: 8px;
  font-family: 'Roboto Mono';
  color: ${props => priceChangeColors[props.$status]};
  ${typographyV2.bodyXs}
`;

export const FlexWrapper = styled.span`
  display: flex;
  gap: 8px;
`;

export const PriceChangeWrapper = styled.span`
  ${typographyV2.bodyXs};
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-soft);
`;

export const DateLabel = styled.span`
  ${typographyV2.bodyXs};
  color: var(--text-soft);
`;

export const EarningsSection = styled.div<{ $isReported: boolean }>`
  ${borderRadius.r2};
  background-color: var(--surface-default);
  padding: 8px 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ $isReported }) => ($isReported ? '24px' : '16px')};
`;

export const EarningsDateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const EarningsDateTitleLabel = styled.span`
  ${typographyV2.titleSm};
  color: var(--text-soft);
`;

export const EarningsDateValueLabel = styled.span`
  ${typographyV2.bodySm};
  color: var(--text-default);
`;

export const EarningsReactionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

export const EarningsReactionTitleLabel = styled.span`
  ${typographyV2.titleSm};
  color: var(--text-soft);
`;

export const EarningsReactionValueLabel = styled.span<{ $isPositive: boolean }>`
  ${typographyV2.bodySm};
  font-family: var(--font-roboto-mono);
  color: ${({ $isPositive }) =>
    $isPositive ? 'var(--viz-bullish)' : 'var(--viz-bearish)'};
`;

export const DescriptionLabel = styled.div<{ $isReported?: boolean }>`
  padding-top: ${({ $isReported }) => ($isReported ? '24px' : '4px')};
  padding-bottom: 4px;
  ${typographyV2.bodyBase};
`;

export const RelationLabel = styled.div`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const PotentialImpactLabel = styled.div`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const TableNameCell = styled.div`
  min-width: 0;
  flex: 1;
  padding: 6px 12px;
`;

export const StyledLoader = styled(SkeletonLoader).attrs({
  areas: [
    {
      styles: {
        width: '100%',
        height: '100px',
        borderRadius: '8px',
      },
    },
  ],
})``;

export const NumberCell = styled.div`
  padding: 6px 12px;
  font-family: var(--font-roboto-mono);
`;

export const SurpriseCell = styled.div<{
  $isPositive: boolean;
  $isZero: boolean;
}>`
  padding: 6px 12px;
  font-family: var(--font-roboto-mono);
  color: ${({ $isPositive, $isZero }) =>
    $isZero
      ? 'var(--text-soft)'
      : $isPositive
      ? 'var(--viz-bullish)'
      : 'var(--viz-bearish)'};
  background-color: ${({ $isPositive, $isZero }) =>
    $isZero
      ? 'var(--surface-default)'
      : $isPositive
      ? 'var(--viz-bullish-soft)'
      : 'var(--viz-bearish-soft)'};
`;
